import React, { useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import { convertMetricToAWG, convertAWGToMetric } from '../../utils/acPowerLogic';
import { translateColor, getColorsForLanguage } from '../../utils/colorUtils';
import CablePreview from '../visualization/CablePreview'; // Importa CablePreview
import * as XLSX from 'xlsx'; // Importa la libreria xlsx

const GeneralSettingsSidebar = ({ settings, setSettings, currentCable, setCurrentCable, cables, setCables, setTerminali, setFileName, setRevision, setSchemaElettrico, setInstallazione }) => {
  const { t } = useI18n();
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showCablePreview, setShowCablePreview] = useState(false); // Nuovo stato per Anteprima Cavo

  const handleImportTerminals = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { range: 1 });

        const importedTerminali = {};
        jsonData.forEach(row => {
          if (row['COD'] && row['NOME']) {
            importedTerminali[row['COD']] = {
              nome: row['NOME'],
              len: parseInt(row['len']) || 0,
              peel: parseInt(row['peel']) || 0
            };
          }
        });

        if (Object.keys(importedTerminali).length > 0) {
          setTerminali(importedTerminali);
          alert(t('settings.terminalsImportedSuccess'));
        } else {
          alert(t('settings.noValidTerminalsFound'));
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Basic Settings - always visible */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            {t('settings.generalSettings')}
          </h2>
          <button 
            className="text-gray-600 hover:text-gray-800 transition-colors text-lg"
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
          >
            {showAdvancedSettings ? '▼' : '▷'}
          </button>
        </div>
        {/* Campi Nome File, Revisione, Schema Elettrico, Installazione */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.fileName')}</label>
            <input
              type="text"
              value={settings.fileName}
              onChange={(e) => setFileName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={t('settings.placeholderFileName')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.revision')}</label>
            <input
              type="number"
              value={settings.revision}
              onChange={(e) => setRevision(Math.max(0, Math.min(99, parseInt(e.target.value) || 0)))}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              min="0"
              max="99"
              placeholder={t('settings.placeholderRevision')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.electricalSchema')}</label>
            <input
              type="text"
              value={settings.schemaElettrico}
              onChange={(e) => setSchemaElettrico(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={t('settings.placeholderSchema')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.installation')}</label>
            <input
              type="text"
              value={settings.installazione}
              onChange={(e) => setInstallazione(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={t('settings.placeholderInstallation')}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.quantityWiring')}</label>
            <input
              type="number"
              value={settings.quantitaCablaggi}
              onChange={(e) => setSettings(prev => ({ ...prev, quantitaCablaggi: parseInt(e.target.value) || 1 }))}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.markCables')}</label>
            <select
              value={settings.marcaCavi}
              onChange={(e) => setSettings(prev => ({ ...prev, marcaCavi: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>{t('settings.no')}</option>
              <option value={1}>{t('settings.yes')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.sectionStandard')}</label>
            <select
              value={settings.useAWG ? 'awg' : 'metric'}
              onChange={(e) => {
                const newUseAWG = e.target.value === 'awg';
                setSettings(prev => ({ ...prev, useAWG: newUseAWG }));

                setCurrentCable(prev => ({
                  ...prev,
                  sezione: newUseAWG ? convertMetricToAWG(prev.sezione) : convertAWGToMetric(prev.sezione)
                }));

                if (cables.length > 0) {
                  setCables(prev => prev.map(cable => ({
                    ...cable,
                    sezione: newUseAWG ? convertMetricToAWG(cable.sezione) : convertAWGToMetric(cable.sezione)
                  })));
                }
              }}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="metric">{t('settings.metric')}</option>
              <option value="awg">{t('settings.american')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.colorLanguage')}</label>
            <select
              value={settings.lingua}
              onChange={(e) => {
                const newLang = e.target.value;
                setSettings(prev => ({ ...prev, lingua: newLang }));

                // Traduci il colore del cavo corrente
                setCurrentCable(prev => ({
                  ...prev,
                  colore: translateColor(prev.colore, newLang)
                }));

                // Traduci tutti i colori dei cavi esistenti
                setCables(prev => prev.map(cable => {
                  if (cable.poli && Array.isArray(cable.poli)) {
                    // Cavo Multipolare
                    return {
                      ...cable,
                      poli: cable.poli.map(polo => ({
                        ...polo,
                        colore: translateColor(polo.colore, newLang)
                      }))
                    };
                  } else {
                    // Cavo Standard
                    return {
                      ...cable,
                      colore: translateColor(cable.colore, newLang)
                    };
                  }
                }));
              }}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="it">{t('settings.italian')}</option>
              <option value="en">{t('settings.english')}</option>
            </select>
          </div>
        </div>
        {/* Impostazioni Avanzate - integrate nel pannello principale */}
        {showAdvancedSettings && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-t pt-4">
              {t('settings.advancedSettings')}
            </h3>
            {/* Sezione Marcature - Senza Marcatura Centrale */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h4 className="text-md font-medium mb-4 text-slate-700">
                {t('settings.markingConfiguration')}
              </h4>

              <div className="bg-white rounded-lg p-4 border border-slate-200 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-medium text-slate-800 mb-1">
                      {t('settings.markingModePerConnection')}
                    </h5>
                    <p className="text-xs text-slate-600">
                      {t('settings.markingModePerConnectionDesc')}
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.marcaturePerCollegamento}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          marcaturePerCollegamento: e.target.checked
                        }))}
                        className="sr-only"
                      />
                      <div className={`
                            w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                            ${settings.marcaturePerCollegamento ? 'bg-blue-600' : 'bg-gray-300'}
                          `}>
                        <div className={`
                              w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                              ${settings.marcaturePerCollegamento ? 'translate-x-5' : 'translate-x-0.5'}
                            `} />
                      </div>
                    </div>
                  </label>
                </div>

                {settings.marcaturePerCollegamento && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">ℹ️</span>
                      <div className="text-sm text-blue-800">
                        <strong>{t('settings.modeActive')}</strong> {t('settings.modeActiveDesc')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview del cavo */}
              <div className="mb-6">
                <CablePreview settings={settings} setSettings={setSettings} />
              </div>

              {/* Posizioni primarie - SENZA MARCATURA CENTRALE */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('settings.markingHeadPosition')}
                  </label>
                  <input
                    type="number"
                    value={settings.posMarcaturaTesta}
                    onChange={(e) => setSettings(prev => ({ ...prev, posMarcaturaTesta: parseInt(e.target.value) || 30 }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                    min={0}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('settings.markingTailPosition')}
                  </label>
                  <input
                    type="number"
                    value={settings.posMarcaturaCoda}
                    onChange={(e) => setSettings(prev => ({ ...prev, posMarcaturaCoda: parseInt(e.target.value) || 30 }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                    min={0}
                  />
                </div>
              </div>

              {/* Marcature secondarie */}
              <div className="border-t border-slate-200 pt-4">
                <h5 className="text-sm font-medium mb-3 text-slate-600">
                  {t('settings.secondaryMarkings')}
                </h5>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center h-[44px]">
                    <label className="flex items-center gap-3 cursor-pointer bg-white border rounded-md px-3 py-2 h-full w-full hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={settings.abilitaMarcature2}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          abilitaMarcature2: e.target.checked
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium">
                        {t('settings.enableHead2Tail2')}
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('settings.secondaryMarkingContent')}
                    </label>
                    <select
                      value={settings.tipoMarcature2}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        tipoMarcature2: e.target.value
                      }))}
                      disabled={!settings.abilitaMarcature2}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 h-[44px] ${!settings.abilitaMarcature2 ? 'bg-gray-100 text-gray-400' : ''
                        }`}
                    >
                      <option value="collegamento">{t('settings.connection')}</option>
                      <option value="siglatura">{t('settings.signature')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('settings.additionalDistance')}
                    </label>
                    <input
                      type="number"
                      value={settings.distanzaMarcature2}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        distanzaMarcature2: parseInt(e.target.value) || 0
                      }))}
                      disabled={!settings.abilitaMarcature2}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 h-[44px] ${!settings.abilitaMarcature2 ? 'bg-gray-100 text-gray-400' : ''
                        }`}
                      min={0}
                      placeholder="10"
                    />
                  </div>
                </div>
              </div>

              {/* Ribaltamenti */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <label className="block text-sm font-medium mb-3">
                  {t('settings.flipMarkings')}
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { key: 'Testa', label: t('settings.head') },
                    { key: 'Centrale', label: t('settings.central') },
                    { key: 'Coda', label: t('settings.tail') }
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center h-[44px]">
                      <label className="flex items-center gap-3 cursor-pointer bg-white border rounded-md px-3 py-2 h-full w-full hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={settings[`ribalta${key}`] === 1}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            [`ribalta${key}`]: e.target.checked ? 1 : 0
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium">{label}</span>
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {t('settings.head2FollowsHead')}
                </p>
              </div>
            </div>

            {/* Sezione Marcatura Centrale e Loop Printing - UNIFICATA */}
            <div className="bg-emerald-50 rounded-lg p-4 mb-6">
              <h4 className="text-md font-medium mb-4 text-emerald-700">
                {t('settings.centralMarkingAndLoop')}
              </h4>

              {/* Marcatura centrale */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  {t('settings.centralMarkingType')}
                </label>
                <select
                  value={settings.marcaturaCentrale}
                  onChange={(e) => setSettings(prev => ({ ...prev, marcaturaCentrale: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="niente">{t('settings.nothing')}</option>
                  <option value="siglatura">{t('settings.signature')}</option>
                  <option value="numerazione">{t('settings.numbering')}</option>
                </select>
              </div>

              {/* Configurazione loop quando marcatura centrale è attiva */}
              {settings.marcaturaCentrale !== 'niente' && (
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-start gap-2 mb-4">
                    <span className="text-emerald-600 text-lg">ℹ️</span>
                    <div>
                      <div className="text-sm font-medium text-emerald-800">
                        {t('settings.loopPrintingAuto')}
                      </div>
                      <div className="text-xs text-emerald-700 mt-1">
                        {t('settings.loopPrintingDesc')}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('settings.loopMode')}
                      </label>
                      <select
                        value={settings.loopMode}
                        onChange={(e) => setSettings(prev => ({ ...prev, loopMode: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="numero">{t('settings.basedOnNumber')}</option>
                        <option value="distanza">{t('settings.basedOnDistance')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {settings.loopMode === 'numero' ? t('settings.loopNumber') : t('settings.loopDistance')}
                      </label>
                      <input
                        type="number"
                        value={settings.loopMode === 'numero' ? settings.loopNum : settings.loopDist}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          [settings.loopMode === 'numero' ? 'loopNum' : 'loopDist']: parseInt(e.target.value) || 0
                        }))}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
                        placeholder={settings.loopMode === 'numero' ? 'N°' : 'mm'}
                        min={settings.loopMode === 'numero' ? 0 : 0}
                        step={settings.loopMode === 'numero' ? 1 : 20}
                      />
                      {settings.loopMode === 'distanza' && (
                        <p className="text-xs text-emerald-600 mt-1">
                          {t('settings.increment20mm')}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-emerald-600 bg-emerald-100 border border-emerald-200 rounded px-2 py-1">
                    {t('settings.loopHowItWorks')}
                  </div>
                </div>
              )}

              {/* Messaggio quando loop è disabilitato */}
              {settings.marcaturaCentrale === 'niente' && (
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">🔒</span>
                    <div className="text-sm text-gray-600">
                      {t('settings.loopDisabled')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sezione Altre Impostazioni */}
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="text-md font-medium mb-4 text-amber-700">
                {t('settings.otherSettings')}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('settings.boldSize')}
                  </label>
                  <input
                    type="number"
                    value={settings.grandezzaGrassetto}
                    onChange={(e) => setSettings(prev => ({ ...prev, grandezzaGrassetto: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-amber-500"
                    min={1}
                    max={10}
                  />
                </div>
              </div>

              {/* Compensazione Lunghezza - esistente */}
              <div className="md:col-span-2 mt-4 pt-4 border-t border-amber-200">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-300">
                  <div>
                    <h5 className="text-sm font-medium text-amber-800 mb-1">
                      {t('settings.lengthCompensation')}
                    </h5>
                    <p className="text-xs text-amber-700">
                      {t('settings.lengthCompensationDesc')}
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.compensazioneLunghezza}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          compensazioneLunghezza: e.target.checked
                        }))}
                        className="sr-only"
                      />
                      <div className={`
                          w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                          ${settings.compensazioneLunghezza ? 'bg-amber-600' : 'bg-gray-300'}
                        `}>
                        <div className={`
                            w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                            ${settings.compensazioneLunghezza ? 'translate-x-5' : 'translate-x-0.5'}
                          `} />
                      </div>
                    </div>
                  </label>
                </div>

                {settings.compensazioneLunghezza && (
                  <div className="mt-3 p-3 bg-amber-100 border border-amber-300 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-600">ℹ️</span>
                      <div className="text-sm text-amber-800">
                        <strong>{t('settings.functionActive')}</strong> {t('settings.lengthCompensationActive')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ✅ NUOVO: Toggle Cavi Neri */}
              <div className="md:col-span-2 mt-4 pt-4 border-t border-amber-200">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-300">
                  <div>
                    <h5 className="text-sm font-medium text-amber-800 mb-1">
                      {t('settings.forceBlackCables')}
                    </h5>
                    <p className="text-xs text-amber-700">
                      {t('settings.forceBlackCablesDesc')}
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.forceBlackCables}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          forceBlackCables: e.target.checked
                        }))}
                        className="sr-only"
                      />
                      <div className={`
                          w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                          ${settings.forceBlackCables ? 'bg-gray-900' : 'bg-gray-300'}
                        `}>
                        <div className={`
                            w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                            ${settings.forceBlackCables ? 'translate-x-5' : 'translate-x-0.5'}
                          `} />
                      </div>
                    </div>
                  </label>
                </div>

                {settings.forceBlackCables && (
                  <div className="mt-3 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">ℹ️</span>
                      <div className="text-sm text-gray-800">
                        <strong>{t('settings.functionActive')}</strong> {t('settings.forceBlackCablesActive')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ✅ NUOVO: Toggle Riduzione Peel per Marcatura Manuale */}
              <div className="md:col-span-2 mt-4 pt-4 border-t border-amber-200">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-300">
                  <div>
                    <h5 className="text-sm font-medium text-amber-800 mb-1">
                      {t('settings.reducePeelManual')}
                    </h5>
                    <p className="text-xs text-amber-700">
                      {t('settings.reducePeelManualDesc')}
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.reducePeelForManualMarking}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          reducePeelForManualMarking: e.target.checked
                        }))}
                        className="sr-only"
                      />
                      <div className={`
                          w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                          ${settings.reducePeelForManualMarking ? 'bg-amber-600' : 'bg-gray-300'}
                        `}>
                        <div className={`
                            w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                            ${settings.reducePeelForManualMarking ? 'translate-x-5' : 'translate-x-0.5'}
                          `} />
                      </div>
                    </div>
                  </label>
                </div>

                {settings.reducePeelForManualMarking && (
                  <div className="mt-3 p-3 bg-amber-100 border border-amber-300 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-600">ℹ️</span>
                      <div className="text-sm text-amber-800">
                        <strong>{t('settings.functionActive')}</strong> {t('settings.reducePeelManualActive')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Toggle per Anteprima Cavo */}
              <div className="md:col-span-2 mt-4 pt-4 border-t border-amber-200">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-300">
                  <div>
                    <h5 className="text-sm font-medium text-amber-800 mb-1">
                      {t('settings.cablePreview')}
                    </h5>
                    <p className="text-xs text-amber-700">
                      {t('settings.cablePreviewDesc')}
                    </p>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={showCablePreview}
                        onChange={(e) => setShowCablePreview(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`
                          w-11 h-6 rounded-full transition-colors duration-200 ease-in-out flex items-center
                          ${showCablePreview ? 'bg-blue-600' : 'bg-gray-300'}
                        `}>
                        <div className={`
                            w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out
                            ${showCablePreview ? 'translate-x-5' : 'translate-x-0.5'}
                          `} />
                      </div>
                    </div>
                  </label>
                </div>

                {/* Contenuto dell'Anteprima Cavo (visibile solo se il toggle è attivo) */}
                {showCablePreview && (
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-inner border border-amber-200">
                    <CablePreview settings={settings} setSettings={setSettings} />
                  </div>
                )}
              </div>

              {/* Sezione Importa Terminali */}
              <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-md font-medium mb-4 text-gray-700">
                  {t('settings.importTerminals')}
                </h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleImportTerminals}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.importTerminalsDesc')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSettingsSidebar;